import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Container, Fab, Typography, Alert, Chip } from '@mui/material';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridCellParams,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import moment from 'moment';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { AsyncDialog, AsyncDialogRef } from '../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { Table, TableRef } from '../../../components/Table/Table';
import { withdrawRequestStatusToInfo } from '../../../constants/tables/withdrawRequest';
import { locations } from '../../../routes/locations';
import { useAppDispatch } from '../../../redux/hooks';
import {
  fetchCompanyWithdrawRequests,
  approveCompanyWithdrawRequest,
  rejectCompanyWithdrawRequest,
} from '../../../redux/modules/b2bThriftPlan/companies/company.actions';
import {
  companySelector,
  companyWithdrawRequestsLoadingSelector,
  approveCompanyWithdrawRequestLoadingSelector,
  rejectCompanyWithdrawRequestLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/companies/company.selectors';
import { WithdrawStatus } from '../../../types/payment';
import { CompanyWithdrawRequestRaw } from '../../../types/b2tRatibiPlus';

export function CompanyWithdrawRequests() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const tableRef = useRef<TableRef>(null);
  const approveDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const rejectDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { companyWithdrawRequests, totalCompanyWithdrawRequests } = useSelector(companySelector);
  const isLoading = useSelector(companyWithdrawRequestsLoadingSelector);
  const isApproveLoading = useSelector(approveCompanyWithdrawRequestLoadingSelector);
  const isRejectLoading = useSelector(rejectCompanyWithdrawRequestLoadingSelector);

  const onAddNew = (): void => {
    if (companyId) {
      navigate(locations.companyWithdrawRequest(Number(companyId), 'create'));
    }
  };

  const onApprove = async (withdrawRequest: CompanyWithdrawRequestRaw) => {
    if (!companyId) return;

    await approveDialogRef.current?.show({
      title: 'Approve Withdraw Request',
      description: `Are you sure you want to approve this withdraw request for amount ${withdrawRequest.amount}?`,
      acceptButtonText: 'Approve',
      rejectButtonText: 'Cancel',
      confirmButtonColor: 'success',
      onConfirm: async () => {
        try {
          await dispatch(
            approveCompanyWithdrawRequest({
              companyId: Number(companyId),
              id: withdrawRequest.id,
            }),
          ).unwrap();
          enqueueSnackbar('Withdraw request approved successfully', { variant: 'success' });
          tableRef.current?.reload();
        } catch (error: any) {
          const errorMessage = error?.message || 'Failed to approve withdraw request';
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      },
    });
  };

  const onReject = async (withdrawRequest: CompanyWithdrawRequestRaw) => {
    if (!companyId) return;

    await rejectDialogRef.current?.show({
      title: 'Reject Withdraw Request',
      description: `Are you sure you want to reject this withdraw request for amount ${withdrawRequest.amount}?`,
      acceptButtonText: 'Reject',
      rejectButtonText: 'Cancel',
      confirmButtonColor: 'error',
      onConfirm: async () => {
        try {
          await dispatch(
            rejectCompanyWithdrawRequest({
              companyId: Number(companyId),
              id: withdrawRequest.id,
            }),
          ).unwrap();
          enqueueSnackbar('Withdraw request rejected successfully', { variant: 'success' });
          tableRef.current?.reload();
        } catch (error: any) {
          const errorMessage = error?.message || 'Failed to reject withdraw request';
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      },
    });
  };

  const onEdit = (withdrawRequest: CompanyWithdrawRequestRaw): void => {
    if (companyId) {
      navigate(locations.companyWithdrawRequest(Number(companyId), withdrawRequest.id.toString()));
    }
  };

  const columns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      width: 150,
      cellClassName: (params: GridCellParams) => {
        const status = String(params.row.status || '')
          .toLowerCase()
          .trim();
        const isPending = status === 'pending';
        // Return disabled class if status is not pending
        return !isPending ? 'actions-cell-disabled' : '';
      },
      getActions: ({ row }: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];
        const status = String(row.status || '')
          .toLowerCase()
          .trim();
        const isPending = status === 'pending';
        const isApproved = status === 'approved' || status === 'approve';
        const isRejected = status === 'rejected' || status === 'reject';

        // Disable menu actions if status is not pending
        const isMenuDisabled = !isPending || isApproveLoading || isRejectLoading;

        // Edit action - shown directly (not in menu) - always enabled regardless of status
        actions.push(
          <GridActionsCellItem
            key={`edit-${row.id}`}
            label="Edit"
            icon={<EditIcon />}
            onClick={() => onEdit(row as CompanyWithdrawRequestRaw)}
            showInMenu={false}
            disabled={!isPending}
          />,
        );

        // Approve action - shown in menu
        actions.push(
          <GridActionsCellItem
            key={`approve-${row.id}`}
            label="Approve"
            onClick={() => onApprove(row as CompanyWithdrawRequestRaw)}
            showInMenu
            disabled={isApproved || isMenuDisabled}
          />,
        );

        // Reject action - shown in menu
        actions.push(
          <GridActionsCellItem
            key={`reject-${row.id}`}
            label="Reject"
            onClick={() => onReject(row as CompanyWithdrawRequestRaw)}
            showInMenu
            disabled={isRejected || isMenuDisabled}
          />,
        );

        // Always return actions to ensure the menu (three dots) is always displayed
        return actions;
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 110,
    },
    {
      field: 'reason',
      headerName: 'Reason',
      width: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(WithdrawStatus),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const statusValue = params.value as string;
        const statusKey = statusValue?.toUpperCase() as WithdrawStatus;
        const info = withdrawRequestStatusToInfo[statusKey] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return statusValue;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={statusValue} />;
      },
    },
    {
      field: 'beneficiaryName',
      headerName: 'Beneficiary Name',
      width: 150,
    },
    {
      field: 'beneficiaryIban',
      headerName: 'IBAN',
      width: 200,
    },
    {
      field: 'beneficiarySwiftCode',
      headerName: 'SWIFT Code',
      width: 120,
    },
    {
      field: 'statusUpdatedAt',
      headerName: 'Status Updated',
      sortable: false,
      type: 'string',
      width: 150,
      valueGetter: (params: any) => params.row?.statusUpdatedAt,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return moment(params.value).format('MM/DD/YYYY HH:mm');
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      sortable: false,
      type: 'string',
      width: 150,
      valueGetter: (params: any) => params.row?.createdAt,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return moment(params.value).format('MM/DD/YYYY HH:mm');
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      sortable: false,
      type: 'string',
      width: 150,
      valueGetter: (params: any) => params.row?.updatedAt,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return moment(params.value).format('MM/DD/YYYY HH:mm');
      },
    },
  ];

  if (!companyId) {
    return (
      <Container component="main" maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">Company Withdraw Requests</Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            Company ID is required
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <>
      |
      <Container component="main" maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">
            Company Withdraw Requests &nbsp;
            <Fab color="primary" size="small" aria-label="Create Withdraw Request" onClick={onAddNew} sx={{ mr: 1 }}>
              <AddIcon />
            </Fab>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Company ID: {companyId}
          </Typography>
        </Box>
        <Table
          ref={tableRef}
          columns={columns}
          fetchItems={fetchCompanyWithdrawRequests}
          rows={companyWithdrawRequests}
          loading={isLoading || isApproveLoading || isRejectLoading}
          rowCount={totalCompanyWithdrawRequests}
          fetchItemsCustomParams={{ companyId: Number(companyId) }}
          initialSortModel={[{ field: 'id', sort: 'desc' }]}
          getRowId={(row) => row.id || `temp-${row.companyId}-${row.createdAt || Date.now()}`}
          toolbarExportProps={{
            csvOptions: { fileName: `withdraw-requests-${Date.now()}` },
            excelOptions: { fileName: `withdraw-requests-${Date.now()}` },
          }}
        />
      </Container>
      <AsyncDialog ref={approveDialogRef} dialog={ConfirmationDialog} />
      <AsyncDialog ref={rejectDialogRef} dialog={ConfirmationDialog} />
    </>
  );
}
